import { offline } from './connectivity';
// One source result/owner at a time; pending request keys survive page reconnection.
export class QuestionSession {
    public changed: () => void = () => {};
    public source: any = null;
    public text = '';
    public situation = '';
    public scope = 'question_only';
    public parent = '';
    public busy = false;
    public message = '';
    public pending: any = null;
    public thread = '';
    private runId = '';
    private token = '';
    private epoch = 0;
    private resumeKey = '';
    private resumeQuestion = '';
    private sources: any[] = [];
    constructor() {
        try {
            for (const key of Object.keys(sessionStorage)) {
                if (!key.startsWith('saju.question.')) continue;
                try { if (JSON.parse(sessionStorage.getItem(key) || 'null')?.version === 2) continue; } catch {}
                sessionStorage.removeItem(key);
            }
        } catch {}
    }
    forget(kind: string, id: string) {
        try {
            for (const key of Object.keys(sessionStorage)) {
                if (!key.startsWith('saju.question.')) continue;
                const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
                if ((kind === 'run' && (key === 'saju.question.' + id || saved?.reference?.run_id === id)) ||
                    (kind === 'profile' && (saved?.reference?.profile_id === id || saved?.profile_id === id))) sessionStorage.removeItem(key);
            }
            sessionStorage.removeItem('saju-run-command');
        } catch { /* Local storage may be disabled; the server fence remains authoritative. */ }
        this.configure(null, this.token);
    }
    private async storageKey(token: string) {
        const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
        return crypto.subtle.importKey('raw', hash, 'AES-GCM', false, ['encrypt', 'decrypt']);
    }
    async configure(run: any, token: string) {
        const id = run?.id || '';
        if (id === this.runId && token === this.token) { this.sources = run?.question_sources || []; return; }
        this.epoch++; this.runId = id; this.token = token; this.sources = run?.question_sources || [];
        this.source = null; this.text = ''; this.situation = ''; this.scope = 'question_only';
        this.parent = ''; this.thread = ''; this.pending = null; this.busy = false; this.message = ''; this.resumeKey = '';
        if (!run || !token) return;
        const epoch = this.epoch;
        try {
            const saved = JSON.parse(sessionStorage.getItem('saju.question.' + id) || 'null');
            // Migrate away from old plaintext buffers without reading them into UI.
            if (saved && saved.version !== 2) sessionStorage.removeItem('saju.question.' + id);
            if (saved?.version === 2) {
                const key = await this.storageKey(token);
                const bytes = await crypto.subtle.decrypt({name: 'AES-GCM', iv: new Uint8Array(saved.iv), additionalData: new TextEncoder().encode(id)}, key, new Uint8Array(saved.data));
                if (epoch !== this.epoch) return;
                const pending = JSON.parse(new TextDecoder().decode(bytes));
                this.pending = pending;
                this.source = this.sources.find(s => JSON.stringify(s.reference) === JSON.stringify(pending.reference));
                this.text = pending.question; this.situation = pending.situation; this.scope = pending.scope; this.parent = pending.parent_id;
                this.message = '전송 확인이 끝나지 않은 질문이 있습니다. 같은 질문 다시 확인을 눌러 주세요.';
            }
        } catch { /* Server remains authoritative if tab storage is unavailable. */ }
        if (epoch !== this.epoch) return;
        if (!this.source) {
            const last = run.questions?.turns?.slice(-1)[0];
            if (last) this.reopen(last);
        }
        this.changed();
    }
    choose(source: any) {
        if (this.busy || this.pending || !source) return;
        if (this.source?.id === source.id) return;
        if (this.text.trim() || this.situation.trim()) {
            this.message = '작성 중인 질문을 보존했습니다. 현재 질문을 보내거나 입력 내용을 비운 뒤 다른 문단을 선택해 주세요.';
            return;
        }
        this.source = source; this.text = ''; this.situation = ''; this.scope = 'question_only'; this.parent = ''; this.thread = ''; this.message = '';
    }
    byAnchor(anchor: string) { return this.sources.find(s => s.anchor === anchor); }
    select(id: string) { this.choose(this.sources.find(s => s.id === id)); }
    reopen(turn: any) {
        if (this.busy || this.pending) return;
        if (this.text.trim() || this.situation.trim()) {
            this.message = '작성 중인 질문을 보존했습니다. 현재 질문을 보내거나 입력 내용을 비운 뒤 이어 묻기를 선택해 주세요.';
            return;
        }
        this.source = this.sources.find(s => JSON.stringify(s.reference) === JSON.stringify(turn.reference));
        this.thread = turn.thread_id; this.parent = turn.id; this.text = ''; this.situation = ''; this.scope = 'question_only';
    }
    async send(runs: any) {
        if (offline()) { this.message = '현재 오프라인입니다. 질문을 전송하지 않았습니다. 연결 후 보내기를 눌러 주세요.'; return; }
        if (!this.source || this.busy || (!this.pending && !this.text.trim())) return;
        const epoch = this.epoch, id = this.runId, token = this.token;
        this.busy = true; this.message = '';
        if (!this.pending) this.pending = { request_key: crypto.randomUUID(), reference: this.source.reference,
            question: this.text, parent_id: this.parent, situation: this.situation, scope: this.scope };
        try {
            const key = await this.storageKey(token), iv = crypto.getRandomValues(new Uint8Array(12));
            if (epoch !== this.epoch) return;
            const data = await crypto.subtle.encrypt({name:'AES-GCM', iv, additionalData:new TextEncoder().encode(id)}, key, new TextEncoder().encode(JSON.stringify(this.pending)));
            if (epoch !== this.epoch) return;
            sessionStorage.setItem('saju.question.' + id, JSON.stringify({version:2,profile_id:this.pending.reference.profile_id,iv:Array.from(iv),data:Array.from(new Uint8Array(data))}));
        } catch { /* Same in-memory key retained; never fall back to plaintext. */ }
        if (epoch !== this.epoch) return;
        try {
            const result = await runs.request('/' + id + '/questions', 'POST', this.pending, token);
            if (epoch !== this.epoch) return;
            this.pending = null;
            this.text = ''; this.situation = '';
            try { sessionStorage.removeItem('saju.question.' + id); } catch {}
            await runs.poll();
            if (epoch !== this.epoch) return;
            const turn = runs.run?.questions?.turns.find((t: any) => t.id === result.question_id);
            this.busy = false;
            if (turn) this.reopen(turn);
            this.message = '질문이 저장되었습니다. 답변 저장 상태를 확인합니다.';
        } catch (error: any) {
            if (epoch !== this.epoch) return;
            // Known rejections have not accepted this key; ambiguous transport/storage errors retain it.
            if ([409,422].includes(error.status)) {
                this.pending = null;
                try { sessionStorage.removeItem('saju.question.' + id); } catch {}
            }
            this.message = [401,403,404].includes(error.status) ? '참조 결과 또는 열람 권한을 확인할 수 없습니다.' :
                error.code === 'QUESTION_ALREADY_WAITING' ? '진행 중인 질문에서 이어 묻거나 계속하기를 선택해 주세요.' :
                '전송을 확인하지 못했습니다. 연결과 입력 범위를 확인하고 같은 질문으로 다시 시도해 주세요.';
        } finally { if (epoch === this.epoch) this.busy = false; }
    }
    async resume(runs: any) {
        if (offline()) { this.message = '현재 오프라인입니다. 연결 후 직접 이어가기를 눌러 주세요.'; return; }
        const control = runs.run?.questions?.control;
        if (!control?.waiting || this.busy) return;
        const epoch = this.epoch, id = this.runId; this.busy = true;
        if (this.resumeQuestion !== control.question_id) { this.resumeKey = ''; this.resumeQuestion = control.question_id; }
        this.resumeKey ||= crypto.randomUUID();
        try {
            await runs.request('/' + id + '/question-resume', 'POST', {request_key: this.resumeKey, question_id: control.question_id}, this.token);
            if (epoch !== this.epoch) return;
            this.resumeKey = ''; await runs.poll();
            if (epoch === this.epoch) this.message = '질문 대기를 해제했습니다. 단계별 진행에서는 다음 장 버튼으로 이어가세요.';
        } catch { if (epoch === this.epoch) this.message = '계속하기를 확인하지 못했습니다. 저장된 상태에서 다시 확인해 주세요.'; }
        finally { if (epoch === this.epoch) this.busy = false; }
    }
}
